import { memo, useRef, useState } from 'react';
import { Autocomplete, Checkbox, Chip, TextField } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { FieldValue } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import { filterByLabel, setSelected } from './valueUtils';

interface ChipsSelectEditorProps {
    ruleId: string;
    values: FieldValue[];
    selectedValues: string[];
    onChange: (value: string[]) => void;
}

const CHECKBOX_ICON = <CheckBoxOutlineBlankIcon fontSize="small" />;
const CHECKBOX_CHECKED_ICON = <CheckBoxIcon fontSize="small" />;
const CHECKBOX_INDETERMINATE_ICON = <IndeterminateCheckBoxIcon fontSize="small" />;
const LISTBOX_PROPS = { style: { maxHeight: 300 } };
const SELECT_ALL_NAME = '__select_all__';
const SELECT_ALL_ROW_STYLE = { fontWeight: 700, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' };

const AUTOCOMPLETE_SX = {
    '& .MuiAutocomplete-inputRoot': { flexWrap: 'wrap', alignItems: 'center', py: 0.5 },
    '& .MuiAutocomplete-endAdornment': { top: '6px', transform: 'none' },
    '& .MuiChip-root': { fontSize: { xs: '0.7rem', sm: '0.8125rem' }, height: { xs: 22, sm: 24 } },
};

const ChipsSelectEditor = memo(function ChipsSelectEditor({ ruleId, values, selectedValues, onChange }: ChipsSelectEditorProps) {
    const { t } = useLanguage();
    const [inputValue, setInputValue] = useState('');
    const filteredNamesRef = useRef<string[]>(values.map(option => option.name));

    const selectedOptions = values.filter(option => selectedValues.includes(option.name));
    const selectAllOption: FieldValue = { name: SELECT_ALL_NAME, label: t('pages.clients.queryBuilder.simple.selectAll') };

    const filterOptions = (options: FieldValue[], state: { inputValue: string }) => {
        const filtered = filterByLabel(options, state.inputValue);
        filteredNamesRef.current = filtered.map(option => option.name);
        return filtered.length > 0 ? [selectAllOption, ...filtered] : filtered;
    };

    const isEveryFilteredSelected = () => filteredNamesRef.current.every(name => selectedValues.includes(name));

    const handleChange = (_event: unknown, newValue: FieldValue[]) => {
        if (newValue.some(option => option.name === SELECT_ALL_NAME)) {
            onChange(setSelected(selectedValues, filteredNamesRef.current, !isEveryFilteredSelected()));
        } else {
            onChange(newValue.map(option => option.name));
        }
    };

    return (
        <Autocomplete
            multiple
            fullWidth
            size="small"
            sx={AUTOCOMPLETE_SX}
            id={`${ruleId}-tags-select`}
            options={values}
            value={selectedOptions}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={(_event, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') setInputValue(newInputValue);
            }}
            filterOptions={filterOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, val) => option.name === val.name}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => {
                if (option.name !== SELECT_ALL_NAME) {
                    return (
                        <li {...props} key={option.name}>
                            <Checkbox icon={CHECKBOX_ICON} checkedIcon={CHECKBOX_CHECKED_ICON} checked={selected} size="small" sx={{ mr: 1 }} />
                            {option.label}
                        </li>
                    );
                }
                const allSelected = filteredNamesRef.current.length > 0 && isEveryFilteredSelected();
                const someSelected = !allSelected && filteredNamesRef.current.some(name => selectedValues.includes(name));
                return (
                    <li {...props} key={SELECT_ALL_NAME} style={SELECT_ALL_ROW_STYLE}>
                        <Checkbox
                            icon={CHECKBOX_ICON}
                            checkedIcon={CHECKBOX_CHECKED_ICON}
                            indeterminateIcon={CHECKBOX_INDETERMINATE_ICON}
                            checked={allSelected}
                            indeterminate={someSelected}
                            size="small"
                            sx={{ mr: 1 }}
                        />
                        {option.label}
                    </li>
                );
            }}
            renderTags={(tagValues, getTagProps) =>
                tagValues.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.name} label={option.label} size="small" />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={selectedOptions.length === 0 ? t('pages.clients.queryBuilder.selectPlaceholder') : undefined}
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                />
            )}
            noOptionsText={t('pages.clients.queryBuilder.simple.noOptionsFound')}
            ListboxProps={LISTBOX_PROPS}
        />
    );
});

export default ChipsSelectEditor;
